/**
 * useUserTimezone — the resolved IANA timezone to render timestamps
 * in, on behalf of the signed-in user.
 *
 * Resolution order:
 *   1. `user.timezone` from `useIdentity()` (set by the user in
 *      Account Settings → Profile, persisted server-side via #547 PR 5,
 *      read back via #723).
 *   2. The browser's resolved timezone via `Intl.DateTimeFormat()`,
 *      used when the user hasn't set a preference, or identity isn't
 *      ready yet, or the stored value is an unparseable string.
 *
 * Returns a non-empty IANA string in every case — callers can pass it
 * directly to `toLocale*String(..., { timeZone })` or to the
 * `format{Date,Time,DateTime}` helpers in `@/lib/utils` without
 * additional guards.
 *
 * Phase 1 of #724 — see the issue body for the broader migration plan.
 * The Profile UI promises *"Used to render timestamps on your account"*;
 * this hook makes that promise honest for the surfaces that consume it.
 */
import { useIdentity } from '@/hooks/useIdentity'

export function useUserTimezone(): string {
  // `useIdentity` is always called (hook order preserved) but throws
  // if the IdentityProvider isn't mounted — true in some tests, in
  // /auth/login screens, and inside error boundaries that catch a
  // higher-level crash. Catch and fall through to the browser default
  // so this hook's "always returns a valid IANA string" contract holds
  // even where identity context is unavailable. The try/catch is OK
  // here because the hook itself runs unconditionally — only the
  // error from its `if (!ctx) throw` branch is swallowed.
  let userTz: string | undefined
  try {
    const { state } = useIdentity()
    userTz =
      state.status === 'ready' ? state.identity.user.timezone?.trim() : undefined
  } catch {
    userTz = undefined
  }
  if (userTz && isValidIanaTimezone(userTz)) return userTz
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/** Validates an IANA timezone string by asking `Intl.DateTimeFormat`
 * to accept it. Returns false for typos, empty strings, and any
 * value the host's ICU table doesn't know — older user records
 * persisted before the Profile UI's IANA-only validation could
 * carry a non-IANA value (e.g. an offset like `UTC+5` or a
 * deprecated zone name). Letting that slip through would crash
 * every consuming `toLocale*String` call with `RangeError`.
 *
 * Cheap (constructs but doesn't format) — safe to call per render. */
function isValidIanaTimezone(tz: string): boolean {
  try {
    // The constructor itself validates the `timeZone` option.
    new Intl.DateTimeFormat(undefined, { timeZone: tz })
    return true
  } catch {
    return false
  }
}
